import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { useNavigate, useParams, useLocation, Outlet } from 'react-router-dom';
import { useTranslation } from '@aimsos/i18n';
import { useAppAuth } from '@aimsos/auth';
import { AlertCircle, Bot, ChevronDown, ChevronUp, Loader2 } from 'lucide-react';
import {
  Badge,
  BreadcrumbNav,
  Button,
  ScrollTabs,
  SearchableSelect,
  StatusMessage,
  Textfield,
  useToast,
} from '@aimsos/ui';
import { AgentKnowledgeProviders } from '../context/knowledge';
import { useAgents } from '../context';
import { useAgentDetail, useAgentInstructionTab } from '../hooks';
import {
  ConversationStarters,
  InstructionsTabPanel,
  KnowledgeTabPanel,
  ToolsTabPanel,
} from '../components';
import AgentPlaygroundChatColumn from '../components/AgentPlaygroundChatColumn.jsx';
import { parseAgentListResponse, parseAgentUpdateResponse } from '../../../shared/utils/apiResponse';
import { listAgents, updateAgent } from '../../../services';
import { buildAgentListBody } from '../constants';
import { mapAgentListItemToUi } from '../utils';

const TABS = ['create', 'configuration', 'knowledge', 'instructions', 'tools'];
const AUTOSAVE_DELAY = 1500;
/** First page size for the header agent switcher (same pattern as chat SearchableSelect). */
const AGENT_SWITCHER_LIST_PAGE_SIZE = 100;

const AGENT_STATUS_LABEL_KEYS = {
  active: 'edit.agentStatus.active',
  disabled: 'edit.agentStatus.disabled',
  draft: 'edit.agentStatus.draft',
  archived: 'edit.agentStatus.archived',
  published: 'edit.agentStatus.published',
};

function agentStatusBadgeProps(statusRaw, t) {
  const key = (statusRaw || '').toLowerCase();
  let variant = 'default';
  if (key === 'active' || key === 'published') variant = 'success';
  else if (key === 'draft') variant = 'neutral';
  else if (key === 'disabled') variant = 'warning';
  else if (key === 'archived') variant = 'secondary';

  const label = AGENT_STATUS_LABEL_KEYS[key]
    ? t(AGENT_STATUS_LABEL_KEYS[key])
    : (typeof statusRaw === 'string' && statusRaw.trim() ? statusRaw.trim() : t('edit.agentStatus.unknown'));

  return { variant, label };
}

const AgentEditPage = () => {
  const { agentId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation('agents');
  const toast = useToast();
  const { userId } = useAppAuth();

  const { updateAgentInList } = useAgents();
  const isNew = agentId === 'new';

  // Fetch agent detail from API (skipped when creating a new agent)
  const { agent: fetchedAgent, loading: detailLoading, error: detailError, mutate: mutateAgent } = useAgentDetail(
    isNew ? null : agentId,
  );

  const agentReady = Boolean(!isNew && !detailLoading && fetchedAgent?.id);
  const instructionTab = useAgentInstructionTab({
    agentId: isNew ? undefined : agentId,
    agentReady,
    isNewAgent: isNew,
    t,
    toast,
  });

  // Derive active tab from the last URL segment — same pattern as WorkspaceDetailPage
  const activeTab = useMemo(() => {
    const segments = location.pathname.split('/');
    const last = segments[segments.length - 1];
    return TABS.includes(last) ? last : 'configuration';
  }, [location.pathname]);

  const [agentName, setAgentName] = useState('');
  const [description, setDescription] = useState('');
  const [chatStarters, setChatStarters] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  const [baseline, setBaseline] = useState(null);
  const [switcherAgents, setSwitcherAgents] = useState([]);
  const [switcherLoading, setSwitcherLoading] = useState(false);
  const autosaveTimeoutRef = useRef(null);
  const autosaveToastIdRef = useRef(null);
  const lastAutosaveAttemptKeyRef = useRef(null);

  // Populate form fields once the API response arrives (only runs once per agent load)
  useEffect(() => {
    if (!fetchedAgent) return;
    const initial = {
      name: fetchedAgent.name || '',
      description: fetchedAgent.description || '',
      starters: Array.isArray(fetchedAgent.starters) ? fetchedAgent.starters : [],
    };
    // Always sync from fetchedAgent — it already has any pending local mutation
    // merged in by useAgentDetail, so we never show stale API data on re-mount.
    setAgentName(initial.name);
    setDescription(initial.description);
    setChatStarters(initial.starters);
    setBaseline(initial);
  }, [fetchedAgent]);

  useEffect(() => {
    if (isNew) {
      setSwitcherAgents([]);
      return undefined;
    }
    let cancelled = false;
    setSwitcherLoading(true);
    listAgents(buildAgentListBody(1, AGENT_SWITCHER_LIST_PAGE_SIZE, '', {}))
      .then((res) => {
        if (cancelled) return;
        const { rows } = parseAgentListResponse(res, t('list.error'));
        setSwitcherAgents(rows.map(mapAgentListItemToUi));
      })
      .catch(() => {
        if (!cancelled) setSwitcherAgents([]);
      })
      .finally(() => {
        if (!cancelled) setSwitcherLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isNew, t]);

  const agentSwitcherOptions = useMemo(() => {
    const rows = (switcherAgents || []).map((a) => {
      const subtitle =
        (typeof a.description === 'string' && a.description.trim()) ||
        (typeof a.model === 'string' && a.model.trim()) ||
        undefined;
      const src = typeof a.photoUrl === 'string' && a.photoUrl.trim() ? a.photoUrl.trim() : undefined;
      return {
        id: a.id,
        name: a.name || a.id || 'Agent',
        subtitle,
        avatarSrc: src,
      };
    });
    if (fetchedAgent?.id && !rows.some((o) => o.id === fetchedAgent.id)) {
      const subtitle =
        (typeof fetchedAgent.description === 'string' && fetchedAgent.description.trim()) ||
        (typeof fetchedAgent.model === 'string' && fetchedAgent.model.trim()) ||
        undefined;
      const src =
        typeof fetchedAgent.photoUrl === 'string' && fetchedAgent.photoUrl.trim()
          ? fetchedAgent.photoUrl.trim()
          : undefined;
      rows.unshift({
        id: fetchedAgent.id,
        name: fetchedAgent.name || fetchedAgent.id,
        subtitle,
        avatarSrc: src,
      });
    }
    return rows;
  }, [switcherAgents, fetchedAgent]);

  const draft = useMemo(
    () => ({
      name: agentName,
      description,
      starters: chatStarters,
    }),
    [agentName, description, chatStarters],
  );

  const hasUnsavedChanges = useMemo(() => {
    if (isNew || !fetchedAgent?.id || !baseline) return false;
    return JSON.stringify(draft) !== JSON.stringify(baseline);
  }, [isNew, fetchedAgent?.id, draft, baseline]);
  const draftKey = useMemo(() => JSON.stringify(draft), [draft]);

  const handleSave = useCallback(async () => {
    if (!fetchedAgent?.id || isSaving) return false;
    // Remember attempted draft so failed requests don't loop forever.
    lastAutosaveAttemptKeyRef.current = draftKey;
    const payload = {
      name: draft.name,
      description: draft.description,
      model: fetchedAgent.model,
      starters: draft.starters,
      metadata: {
        imageUrl: fetchedAgent.photoUrl || '',
      },
      tenant_id: fetchedAgent.tenantId,
      owner: fetchedAgent.owner,
      created_by: fetchedAgent.createdBy,
      status: fetchedAgent.status,
      type: fetchedAgent.type,
      version: fetchedAgent.version,
      scope: fetchedAgent.scope,
      tags: fetchedAgent.tags,
      category: fetchedAgent.category,
      image: fetchedAgent.image,
      signature: fetchedAgent.signature,
    };

    setIsSaving(true);
    autosaveToastIdRef.current = `agents-autosave-${fetchedAgent.id}`;
    toast.info({
      id: autosaveToastIdRef.current,
      title: t('edit.autosave.savingTitle'),
      message: t('edit.autosave.savingMessage'),
      canClose: true,
    });

    try {
      const res = await updateAgent(fetchedAgent.id, payload);
      parseAgentUpdateResponse(res, t('edit.autosave.errorMessage'));
      // Write the saved values into the local mutation cache so re-mounting
      // (navigate away + back) shows the correct data even when the backend
      // has a read-after-write lag.
      mutateAgent({ name: draft.name, description: draft.description, starters: draft.starters });
      // Also update the shared AgentsContext so the list and any other consumer
      // that reads from context immediately reflect the new values.
      updateAgentInList(fetchedAgent.id, { name: draft.name, description: draft.description, starters: draft.starters });
      setBaseline(draft);
      toast.success({
        title: t('edit.autosave.savedTitle'),
        message: t('edit.autosave.savedMessage'),
        canClose: true,
      });
      return true;
    } catch (err) {
      toast.error({
        title: t('edit.autosave.errorTitle'),
        message: err?.message ?? t('edit.autosave.errorMessage'),
        canClose: true,
      });
      return false;
    } finally {
      setIsSaving(false);
    }
  }, [fetchedAgent, isSaving, draft, draftKey, mutateAgent, updateAgentInList, toast, t]);

  useEffect(() => {
    if (!hasUnsavedChanges || !fetchedAgent?.id) return undefined;
    if (lastAutosaveAttemptKeyRef.current === draftKey) return undefined;
    if (autosaveTimeoutRef.current) clearTimeout(autosaveTimeoutRef.current);
    autosaveTimeoutRef.current = setTimeout(() => {
      void handleSave();
    }, AUTOSAVE_DELAY);
    return () => {
      if (autosaveTimeoutRef.current) clearTimeout(autosaveTimeoutRef.current);
    };
  }, [hasUnsavedChanges, fetchedAgent?.id, draftKey, handleSave]);

  const displayName = agentName || fetchedAgent?.name || (isNew ? t('edit.newAgent') : '');

  const handleNavigateAway = useCallback(
    async (path) => {
      if (hasUnsavedChanges) {
        if (autosaveTimeoutRef.current) {
          clearTimeout(autosaveTimeoutRef.current);
          autosaveTimeoutRef.current = null;
        }
        const saved = await handleSave();
        if (!saved) return;
      }
      navigate(path);
    },
    [hasUnsavedChanges, handleSave, navigate],
  );

  const onBreadcrumbNavigate = useCallback(
    (path) => void handleNavigateAway(path),
    [handleNavigateAway],
  );

  const handleAgentSwitcherSelect = useCallback(
    (opt) => {
      const id = opt?.id;
      if (!id || String(id) === String(agentId)) return;
      void handleNavigateAway(`/agents/${id}/${activeTab}`);
    },
    [agentId, activeTab, handleNavigateAway],
  );

  const breadcrumbItems = useMemo(
    () => [
      { label: t('edit.breadcrumb'), path: '/agents' },
      { label: displayName || agentId },
    ],
    [t, displayName, agentId],
  );

  const statusBadge = useMemo(
    () => agentStatusBadgeProps(fetchedAgent?.status, t),
    [fetchedAgent?.status, t],
  );

  const renderTabContent = () => {
    switch (activeTab) {
      case 'knowledge':
        if (isNew || !fetchedAgent?.id) {
          return <KnowledgeTabPanel t={t} toast={toast} disabled />;
        }
        return (
          <AgentKnowledgeProviders agent={fetchedAgent} userId={userId}>
            <KnowledgeTabPanel t={t} toast={toast} disabled={false} />
          </AgentKnowledgeProviders>
        );

      case 'instructions':
        return (
          <InstructionsTabPanel
            t={t}
            instruction={instructionTab.instruction}
            onInstructionChange={instructionTab.handleInstructionChange}
            disabled={isNew || !fetchedAgent?.id}
            loading={instructionTab.loading}
            fetchError={instructionTab.fetchError}
            showEmptyNotice={instructionTab.showEmptyNotice}
          />
        );

      case 'tools':
        return (
          <ToolsTabPanel
            t={t}
            agentId={fetchedAgent?.id}
            agentReady={agentReady}
            disabled={isNew || !fetchedAgent?.id}
          />
        );

      case 'configuration':
        return (
          <div className="flex flex-col gap-6">
            {/* Agent Name — Textfield from @aimsos/ui (floating label) */}
            <Textfield
              label={t('edit.configuration.agentName')}
              value={agentName}
              onChange={setAgentName}
              placeholder={t('edit.configuration.agentNamePlaceholder')}
            />

            {/* Description — textarea with same ann-style floating label */}
            <div className="flex flex-col gap-1.5">
              <label className="text-sm font-medium text-content-secondary">
                {t('edit.configuration.description')}
              </label>
              <div className="relative rounded-lg border border-edge-strong bg-surface px-3 py-2.5 focus-within:ring-2 focus-within:ring-[#2173FF]">
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder={t('edit.configuration.descriptionPlaceholder')}
                  rows={5}
                  className="w-full resize-none bg-transparent text-sm text-content placeholder:text-content-faint focus:outline-none"
                />
              </div>
            </div>

            <ConversationStarters
              label={t('edit.configuration.chatStarters')}
              placeholder={t('edit.configuration.chatStarterPlaceholder')}
              value={chatStarters}
              onChange={setChatStarters}
            />
          </div>
        );

      default:
        return (
          <div className="flex flex-1 flex-col items-center justify-center gap-2 py-16 text-center">
            <Bot className="h-8 w-8 text-content-faint" />
            <p className="text-sm text-content-muted">
              {t(`edit.tabs.${activeTab}`)} — coming soon
            </p>
          </div>
        );
    }
  };

  if (!isNew && detailLoading) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-3 text-content-muted">
        <Loader2 className="h-6 w-6 animate-spin" />
        <p className="text-sm">{t('edit.loading')}</p>
      </div>
    );
  }

  if (!isNew && detailError) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-4 p-8">
        <StatusMessage
          icon={AlertCircle}
          iconClassName="text-red-500"
          title={t('edit.loadError')}
          subtitle={detailError}
        />
        <Button type="button" variant="secondary" onClick={() => navigate(0)}>
          {t('edit.retry')}
        </Button>
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col overflow-hidden bg-surface-base">
      <main className="flex min-h-0 flex-1 flex-col overflow-hidden bg-surface-base">
        {/* Fixed header: breadcrumb + page title + tabs */}
        <div className="flex w-full shrink-0 flex-col gap-4 p-6 pb-0">
          <BreadcrumbNav
            className="min-w-0"
            items={breadcrumbItems}
            onNavigate={onBreadcrumbNavigate}
          />

          <div className="flex items-start justify-between gap-4 px-0 py-1">
            <div className="flex min-w-0 flex-1 items-start gap-3">
              <div className="mt-0.5 shrink-0">
                <Bot className="h-5 w-5" aria-hidden />
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex min-w-0 flex-wrap items-center gap-2">
                  <h1 className="min-w-0 max-w-full truncate text-xl font-semibold text-content">
                    {displayName || agentId}
                  </h1>
                  {!isNew ? (
                    <>
                      <Badge variant={statusBadge.variant} size="small" className="!rounded-full shrink-0">
                        {statusBadge.label}
                      </Badge>
                      <SearchableSelect
                        className="min-w-0 shrink-0"
                        options={agentSwitcherOptions}
                        value={agentId || ''}
                        onSelect={handleAgentSwitcherSelect}
                        isLoading={switcherLoading}
                        debounceMs={0}
                        placeholder={t('edit.agentSwitcher.searchPlaceholder')}
                        searchPlaceholder={t('edit.agentSwitcher.searchPlaceholder')}
                        emptyMessage={t('edit.agentSwitcher.emptyMessage')}
                        menuListMaxHeight="18rem"
                        renderTrigger={({ isOpen, disabled }) => (
                          <button
                            type="button"
                            disabled={disabled}
                            aria-label={t('edit.agentSwitcher.ariaLabel')}
                            aria-haspopup="menu"
                            aria-expanded={isOpen}
                            className={[
                              'inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md border border-edge-strong bg-surface',
                              'text-content-secondary transition-colors hover:bg-surface-hover hover:text-content',
                              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-gray-900',
                              disabled ? 'pointer-events-none opacity-50' : '',
                            ]
                              .filter(Boolean)
                              .join(' ')}
                          >
                            {isOpen ? (
                              <ChevronUp className="size-4 shrink-0" aria-hidden />
                            ) : (
                              <ChevronDown className="size-4 shrink-0" aria-hidden />
                            )}
                          </button>
                        )}
                      />
                    </>
                  ) : null}
                </div>
              </div>
            </div>
          </div>

          <ScrollTabs>
            {TABS.map((tab) => (
              <ScrollTabs.Item
                key={tab}
                active={activeTab === tab}
                onClick={() => navigate(tab)}
              >
                {t(`edit.tabs.${tab}`)}
              </ScrollTabs.Item>
            ))}
          </ScrollTabs>
        </div>

        {/* Two-column content: form left, preview right */}
        <div className="flex min-h-0 flex-1 overflow-hidden px-6">
          {/* Left panel — scrollable tab content */}
          <div className="flex min-h-0 w-1/2 flex-col overflow-y-auto border-r border-edge p-6">
            {renderTabContent()}
          </div>

          {/* Right panel — @aimsos/chat playground (nested provider, same WS/API as shell when embedded) */}
          <div className="flex min-h-0 w-1/2 min-w-0 flex-col border-l border-edge bg-surface">
            <AgentPlaygroundChatColumn
              t={t}
              agent={isNew ? null : fetchedAgent}
              chatStarters={chatStarters}
            />
          </div>
        </div>
      </main>
      {/* Required so React Router resolves the active child tab route */}
      <Outlet />
    </div>
  );
};

export default AgentEditPage;
